import React from "react";
import { Link, useParams } from "react-router-dom";
import { AppShell } from "../components/Nav";
import ScoreMeter from "../components/ScoreMeter";
import VideoPoster from "../components/VideoPoster";
import VibeTag from "../components/VibeTag";
import VoiceCallButton from "../components/VoiceCallButton";

export default function ProfileDetail() {
  const { id } = useParams();
  const [p, setP] = React.useState(undefined);
  const [showReport, setShowReport] = React.useState(false);

  React.useEffect(() => {
    fetch(`/api/profile/${id}`)
      .then(r => r.ok ? r.json() : null)
      .then(data => setP(data))
      .catch(() => setP(null));
  }, [id]);

  if (p === undefined) {
    return (
      <AppShell>
        <div className="px-6 py-20 text-center text-white/40">
          Loading profile...
        </div>
      </AppShell>
    );
  }

  if (p === null) {
    return (
      <AppShell>
        <div className="px-6 py-20 text-center text-white/40">
          Profile not found.
        </div>
      </AppShell>
    );
  }

  return (
    <AppShell>
      <div className="px-6 md:px-12 py-10 md:py-14">
        <div className="grid md:grid-cols-[1.1fr_1fr] gap-8 mb-10">
          {/* Hero */}
          <div className="relative rounded-3xl overflow-hidden ring-1 ring-white/10 h-[540px]">
            {p.image && <img src={p.image} alt={p.name || ''} className="absolute inset-0 h-full w-full object-cover" />}
            <div className="absolute inset-0 bg-gradient-to-t from-[#05070d] via-[#05070d]/35 to-transparent" />
            <div className="absolute top-5 left-5 flex items-center gap-2">
              <span className="glass rounded-full px-3 py-1 text-[10px] uppercase tracking-[0.24em] text-[#F2DE94]">
                Shortlist
              </span>
            </div>
            <div className="absolute bottom-6 left-6 right-6">
              <div className="font-display text-5xl leading-none">
                {p.name}
                <span className="text-white/55 text-2xl font-body ml-2">{p.age}</span>
              </div>
              <div className="text-white/70 mt-2">
                {[p.occupation, p.city].filter(Boolean).join(' · ')}
              </div>
              <p className="font-display italic text-lg mt-3 max-w-md">
                "{p.headline || ''}"
              </p>
            </div>
          </div>

          {/* Meta column */}
          <div className="space-y-5">
            {/* Intro video */}
            <div>
              <div className="text-[11px] tracking-[0.24em] uppercase text-[#D4AF37] mb-3">
                Intro video
              </div>
              {(p.intro_video_url || p.video_url || p.video) ? (
                <div className="flex justify-center">
                  <div className="relative rounded-2xl overflow-hidden ring-1 ring-white/10 bg-black w-[170px] max-w-full aspect-[9/16]">
                    <video
                      poster={p.videoPoster || p.image}
                      controls
                      playsInline
                      preload="metadata"
                      className="absolute inset-0 w-full h-full object-cover bg-black"
                    >
                      <source src={p.intro_video_url || p.video_url || p.video} type="video/mp4" />
                    </video>
                  </div>
                </div>
              ) : p.hasVideo ? (
                <VideoPoster src={p.videoPoster} label="Intro · 0:18" duration="0:18" className="h-64" />
              ) : (
                <div className="glass rounded-2xl h-40 flex flex-col items-center justify-center gap-2 text-white/25">
                  <span className="text-3xl">▶</span>
                  <span className="text-sm">No intro video yet</span>
                </div>
              )}
            </div>

            {/* Compatibility */}
            <div className="glass rounded-3xl p-6">
              <div className="text-[11px] tracking-[0.24em] uppercase text-[#D4AF37] mb-4">
                Compatibility with you
              </div>
              <div className="flex items-baseline gap-3 mb-5">
                <div className="font-display text-5xl text-gradient-gold">{p.compatibility}</div>
                <div className="text-white/55 text-sm">/ 100 · recalculated each round</div>
              </div>
              <div className="space-y-3">
                {Object.entries(p.scores || {}).slice(0, 4).map(([k, v]) => (
                  <ScoreMeter key={k} label={k} value={v} tone="gold" compact />
                ))}
              </div>
            </div>

            {(p.birth_month || p.birth_day) && (
              <ZodiacBadge month={p.birth_month} day={p.birth_day} />
            )}
          </div>
        </div>

        {/* Prompts */}
        <div className="grid md:grid-cols-2 gap-5 mb-10">
          {(p.prompts || []).map((pr) => (
            <div key={pr.q} className="glass rounded-3xl p-6">
              <div className="text-[11px] tracking-[0.24em] uppercase text-[#D4AF37] mb-2">
                Prompt
              </div>
              <div className="font-display text-lg text-white/85">{pr.q}</div>
              <div className="hairline my-4" />
              <p className="font-display italic text-xl leading-snug">"{pr.a}"</p>
            </div>
          ))}
        </div>

        {/* Tags + flags */}
        <div className="grid md:grid-cols-3 gap-5">
          <div className="glass rounded-3xl p-6">
            <div className="text-[11px] tracking-[0.24em] uppercase text-[#D4AF37] mb-3">Vibe</div>
            <div className="flex flex-wrap gap-1.5">
              {(p.tags || []).map((t) => (
                <VibeTag key={t} tone="gold">{t}</VibeTag>
              ))}
            </div>
            <div className="hairline my-4" />
            <div className="text-[11px] tracking-[0.24em] uppercase text-[#D4AF37] mb-3">Interests</div>
            <div className="flex flex-wrap gap-1.5">
              {(p.interests || []).map((i) => (
                <VibeTag key={i}>{i}</VibeTag>
              ))}
            </div>
          </div>
          <div className="glass rounded-3xl p-6">
            <div className="text-[11px] tracking-[0.24em] uppercase text-[#8FD6A6] mb-3">Green flags</div>
            <ul className="space-y-2 text-sm text-white/85">
              {(p.greenFlags || []).map((g) => (
                <li key={g} className="flex gap-2 items-start">
                  <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-[#4AA36A]" />
                  {g}
                </li>
              ))}
            </ul>
          </div>
          <div className="glass rounded-3xl p-6">
            <div className="text-[11px] tracking-[0.24em] uppercase text-[#F5A8B5] mb-3">Gentle notes</div>
            <ul className="space-y-2 text-sm text-white/85">
              {(p.cautions || []).map((c) => (
                <li key={c} className="flex gap-2 items-start">
                  <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-[#B93A50]" />
                  {c}
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="mt-10 flex items-center justify-end gap-3">
          <Link to="/discovery" className="rounded-full border border-white/15 hover:border-white/35 px-5 py-3 text-sm text-white/85">
            Back to pool
          </Link>
          <Link to="/compatibility" data-testid="profile-breakdown" className="rounded-full border border-[rgba(212,175,55,0.4)] text-[#F2DE94] px-5 py-3 text-sm">
            See breakdown
          </Link>
          <VoiceCallButton targetUserId={p.id} />
          <Link to="/chat" data-testid="profile-message" className="rounded-full bg-gradient-gold text-[#080B14] font-semibold px-6 py-3 glow-gold-soft">
            Send a message
          </Link>
        </div>

        <div className="mt-6 flex justify-end">
          <button
            onClick={() => setShowReport(true)}
            className="text-xs text-white/25 hover:text-red-400 transition-colors"
          >
            Report {p.name || 'this user'}
          </button>
        </div>
      </div>
      {showReport && <ReportModal targetId={p.id} targetName={p.name} onClose={() => setShowReport(false)} />}
    </AppShell>
  );
}

function ReportModal({ targetId, targetName, onClose }) {
  const REASONS = [
    "Inappropriate content",
    "Harassment or threatening behavior",
    "Fake or misleading profile",
    "Spam",
    "Underage user",
    "Other",
  ];
  const [reason, setReason]     = React.useState("");
  const [details, setDetails]   = React.useState("");
  const [submitting, setSubmitting] = React.useState(false);
  const [done, setDone]         = React.useState(false);
  const [err, setErr]           = React.useState("");

  const submit = async () => {
    if (!reason) { setErr("Please select a reason"); return; }
    setSubmitting(true); setErr("");
    try {
      const token = localStorage.getItem("bm_token") || localStorage.getItem("token") || "";
      const res = await fetch("/api/report", {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ reported_user_id: targetId, reason, details }),
      });
      if (!res.ok) { const d = await res.json().catch(() => ({})); throw new Error(d.detail || "Failed"); }
      setDone(true);
    } catch (e) { setErr(e.message); }
    finally { setSubmitting(false); }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="glass rounded-3xl w-full max-w-sm p-8 relative">
        <button onClick={onClose} aria-label="Close"
          className="absolute top-4 right-4 h-8 w-8 flex items-center justify-center rounded-full bg-white/5 hover:bg-white/10 text-white/60 hover:text-white transition-colors text-sm">✕</button>
        {done ? (
          <div className="text-center py-4">
            <div className="text-3xl mb-3">✓</div>
            <div className="font-display text-xl mb-2">Report submitted</div>
            <p className="text-white/50 text-sm mb-6">Our safety team reviews reports within 24 hours.</p>
            <button onClick={onClose} className="rounded-full bg-gradient-gold text-[#080B14] font-semibold px-6 py-2.5 text-sm">Done</button>
          </div>
        ) : (
          <>
            <div className="text-[11px] tracking-[0.24em] uppercase text-red-400 mb-3">Report</div>
            <div className="font-display text-xl mb-5">Report {targetName || "this user"}</div>
            {err && <p className="text-red-400 text-sm mb-3">{err}</p>}
            <div className="space-y-2 mb-4">
              {REASONS.map((r) => (
                <button key={r} type="button" onClick={() => setReason(r)}
                  className={`w-full text-left rounded-xl px-4 py-3 text-sm border transition-all ${
                    reason === r ? "border-red-500/40 bg-red-500/[0.08] text-red-300" : "border-white/8 text-white/55 hover:border-white/20"
                  }`}>{r}</button>
              ))}
            </div>
            <textarea value={details} onChange={(e) => setDetails(e.target.value)}
              placeholder="Additional details (optional)" rows={3}
              className="w-full bg-white/[0.03] border border-white/10 rounded-xl px-4 py-3 text-sm text-white placeholder:text-white/25 outline-none resize-none mb-5" />
            <button onClick={submit} disabled={submitting || !reason}
              className="w-full rounded-xl bg-red-500/20 hover:bg-red-500/30 border border-red-500/30 text-red-300 font-semibold py-3 text-sm transition-colors disabled:opacity-40">
              {submitting ? "Submitting…" : "Submit Report"}
            </button>
          </>
        )}
      </div>
    </div>
  );
}

function getZodiac(month, day) {
  const m = parseInt(month);
  const d = parseInt(day);
  if (!m || !d) return null;
  const cutoffs = [19, 18, 20, 19, 20, 20, 22, 22, 22, 22, 21, 21];
  const signs   = ["Aquarius","Pisces","Aries","Taurus","Gemini","Cancer","Leo","Virgo","Libra","Scorpio","Sagittarius","Capricorn"];
  const symbols = ["♒","♓","♈","♉","♊","♋","♌","♍","♎","♏","♐","♑"];
  const idx  = m - 1;
  const pick = d <= cutoffs[idx] ? idx : (idx + 1) % 12;
  return { sign: signs[pick], symbol: symbols[pick] };
}

function ZodiacBadge({ month, day }) {
  const z = getZodiac(month, day);
  if (!z) return null;
  return (
    <div className="glass rounded-3xl p-5">
      <div className="text-[11px] tracking-[0.24em] uppercase text-[#D4AF37] mb-3">Zodiac</div>
      <div className="flex items-center gap-3">
        <span className="h-10 w-10 rounded-xl bg-[rgba(212,175,55,0.1)] ring-1 ring-[rgba(212,175,55,0.25)] flex items-center justify-center text-xl text-[#F2DE94]">
          {z.symbol}
        </span>
        <div>
          <div className="font-display text-xl">{z.sign}</div>
          <div className="text-white/40 text-xs mt-0.5">Zodiac energy</div>
        </div>
      </div>
    </div>
  );
}
